import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, type To } from "react-router";

import { cn } from "@/lib/utils";

/**
 * Two-piece portal that lets list pages "teleport" their toolbar buttons
 * into the global top bar that lives in `SidebarLayout`.
 *
 * Usage:
 *   1. Layout wraps everything in <PageActionsProvider>
 *   2. Layout renders <PageActionsSlot /> wherever the buttons should land
 *   3. Each page renders <PageActions>{buttons}</PageActions>
 */

type PageActionsContextValue = {
  containerEl: HTMLElement | null;
  setContainerEl: (el: HTMLElement | null) => void;
  trailingContainerEl: HTMLElement | null;
  setTrailingContainerEl: (el: HTMLElement | null) => void;
};

const PageActionsContext = createContext<PageActionsContextValue | null>(null);

export const PageActionsProvider = ({ children }: { children: ReactNode }) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [trailingContainerEl, setTrailingContainerEl] =
    useState<HTMLElement | null>(null);
  return (
    <PageActionsContext.Provider
      value={{
        containerEl,
        setContainerEl,
        trailingContainerEl,
        setTrailingContainerEl,
      }}
    >
      {children}
    </PageActionsContext.Provider>
  );
};

/** Mount once in the layout: this is where teleported children land. */
export const PageActionsSlot = ({ className }: { className?: string }) => {
  const ctx = useContext(PageActionsContext);
  if (!ctx) {
    throw new Error(
      "PageActionsSlot must be used inside a <PageActionsProvider>",
    );
  }
  return (
    <div
      ref={(el) => ctx.setContainerEl(el)}
      data-slot="page-actions"
      className={className}
    />
  );
};

/**
 * Render-once placeholder: pages use this in place of <TopToolbar>.
 * Children are portaled into the slot via React's `createPortal`.
 *
 * If used outside the provider (e.g. on a show page that doesn't yet
 * adopt the new layout), the children render inline as a fallback so we
 * don't accidentally drop the actions.
 */
export const PageActions = ({ children }: { children: ReactNode }) => {
  const ctx = useContext(PageActionsContext);
  // Force a re-render once the slot mounts/unmounts so the portal target
  // becomes available even when this component mounts before the slot.
  const [, setReady] = useState(0);
  useEffect(() => {
    if (!ctx?.containerEl) {
      setReady((n) => n + 1);
    }
  }, [ctx?.containerEl]);

  if (!ctx) return <>{children}</>;
  if (!ctx.containerEl) return null;
  return createPortal(children, ctx.containerEl);
};

/** Right side of the header, after the search bar (e.g. show-page ⋮ menu). */
export const PageActionsTrailingSlot = ({
  className,
}: {
  className?: string;
}) => {
  const ctx = useContext(PageActionsContext);
  if (!ctx) {
    throw new Error(
      "PageActionsTrailingSlot must be used inside a <PageActionsProvider>",
    );
  }
  return (
    <div
      ref={(el) => ctx.setTrailingContainerEl(el)}
      data-slot="page-actions-trailing"
      className={className}
    />
  );
};

export const PageActionsTrailing = ({ children }: { children: ReactNode }) => {
  const ctx = useContext(PageActionsContext);
  const [, setReady] = useState(0);
  useEffect(() => {
    if (!ctx?.trailingContainerEl) {
      setReady((n) => n + 1);
    }
  }, [ctx?.trailingContainerEl]);

  if (!ctx) return <>{children}</>;
  if (!ctx.trailingContainerEl) return null;
  return createPortal(children, ctx.trailingContainerEl);
};

/**
 * Tiny header label used inside <PageActions> — e.g. "Leads".
 * Prefer list totals in `ModuleSearchField` placeholders, not here.
 * Pass `count` only for rare explicit cases (not list directory totals).
 */
export const PageTitle = ({
  label,
  count,
}: {
  label: string;
  /** Only shown when explicitly passed — do not use for list totals (put those in ModuleSearchField). */
  count?: number | null;
}) => {
  return (
    <h1 className="mr-2 flex items-baseline gap-1.5 text-sm font-semibold whitespace-nowrap">
      <span>{label}</span>
      {count != null ? (
        <span className="text-xs font-normal text-muted-foreground tabular-nums">
          ({count.toLocaleString()})
        </span>
      ) : null}
    </h1>
  );
};

export type PageBreadcrumbItem = {
  label: string;
  /** Where this level goes. The last item is where you are and has none. */
  to?: To;
  /** Router state for the link (e.g. to keep a list's filters). */
  state?: unknown;
};

/**
 * Where you are, same everywhere: "Section / Place". The first item is the
 * section (as in the sidebar) and takes you back to it; the last is this page
 * (a filter, a record, "New …"). No icons. Long names are cut, full on hover.
 */
export const PageBreadcrumb = ({ items }: { items: PageBreadcrumbItem[] }) => {
  const visible = items.filter((item) => item.label.trim());
  return (
    <nav aria-label="Breadcrumb" className="mr-2 min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm whitespace-nowrap">
        {visible.map((item, index) => {
          const last = index === visible.length - 1;
          return (
            <li
              key={`${index}-${item.label}`}
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                !last && "shrink-0",
              )}
            >
              {index > 0 ? (
                <span aria-hidden className="text-muted-foreground">
                  /
                </span>
              ) : null}
              {last || !item.to ? (
                <h1
                  title={item.label}
                  aria-current={last ? "page" : undefined}
                  className={cn(
                    "truncate",
                    last
                      ? "max-w-[18rem] font-semibold text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  {item.label}
                </h1>
              ) : (
                <Link
                  to={item.to}
                  state={item.state}
                  viewTransition
                  className="rounded-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Responsive label for header toolbar controls (PageActions).
 *
 * Pattern: keep a single horizontal row (slot scrolls); collapse text so
 * icon+aria-label remain on narrow viewports.
 * - `primary` (create CTAs): label from `sm` up
 * - `secondary` (filters, view toggles): label from `md` up
 */
export const ToolbarLabel = ({
  children,
  priority = "secondary",
}: {
  children: ReactNode;
  priority?: "primary" | "secondary";
}) => (
  <span
    className={priority === "primary" ? "hidden sm:inline" : "hidden md:inline"}
  >
    {children}
  </span>
);

/** Cluster of actions on the right side of PageActions — never wrap. */
export const PAGE_ACTIONS_CLUSTER =
  "ml-auto flex shrink-0 flex-nowrap items-center gap-2";
